import { tWithKeyAttr as t } from "../../i18nHelper";
import React, {
  Dispatch,
  SetStateAction,
  useState,
  useRef,
  useEffect,
} from "react";
import styles from "./checkboxDropdown.module.css";
import { INumberOption } from "../../types/types";

const CheckboxDropdown: React.FC<{
  options: INumberOption[];
  title: string;
  selectedValues: number[];
  onChange: Dispatch<SetStateAction<number[]>>;
  usersCategory: string;
  showSearch?: boolean;
  searchPlaceholder?: string;
  placeholder?: string;
  onOpen?: () => void;
}> = ({
  options,
  title,
  selectedValues,
  usersCategory,
  onChange,
  showSearch = false,
  searchPlaceholder,
  placeholder,
  onOpen,
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const [selectedLabels, setSelectedLabels] = useState<string>("");
  const [searchQuery, setSearchQuery] = useState<string>("");

  const toggleDropdown = () => {
    if (!isOpen && onOpen) {
      onOpen();
    }
    setIsOpen((prev) => !prev);
  };

  const handleCheckboxChange = (value: number) => {
    const newSelectedValues = selectedValues.includes(value)
      ? selectedValues.filter((v) => v !== value)
      : [...selectedValues, value];
    onChange(newSelectedValues);
  };

  const handleClear = () => {
    onChange([]);
  };

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target as Node)
      ) {
        setIsOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  useEffect(() => {
    const selected = options
      .filter((opt) => selectedValues.includes(Number(opt.value)))
      .map((opt) => opt.label)
      .join(", ");
    setSelectedLabels(selected);
  }, [options, selectedValues]);

  // איפוס החיפוש כשסוגרים את ה-dropdown
  useEffect(() => {
    if (!isOpen) {
      setSearchQuery("");
    }
  }, [isOpen]);

  // סינון האפשרויות לפי החיפוש
  const filteredOptions =
    showSearch && searchQuery.trim()
      ? options.filter((opt) =>
          opt.label?.toLowerCase().includes(searchQuery.toLowerCase())
        )
      : options;

  return (
    <div className={styles.container} ref={dropdownRef}>
      <div className={styles.comboBox} onClick={toggleDropdown}>
        <span>{selectedLabels || placeholder || t("SELECT")}</span>
        <svg
          height="20"
          width="20"
          viewBox="0 0 20 20"
          aria-hidden="true"
          focusable="false"
          className="css-tj5bde-Svg"
        >
          <path
            fill="#CCCCCC"
            d="M4.516 7.548c0.436-0.446 1.043-0.481 1.576 0l3.908 3.747 3.908-3.747c0.533-0.481 1.141-0.446 1.574 0 0.436 0.445 0.408 1.197 0 1.615-0.406 0.418-4.695 4.502-4.695 4.502-0.217 0.223-0.502 0.335-0.787 0.335s-0.57-0.112-0.789-0.335c0 0-4.287-4.084-4.695-4.502s-0.436-1.17 0-1.615z"
          />
        </svg>
      </div>

      {isOpen && (
        <div className={styles.dropdown}>
          {showSearch && (
            <div className={styles.searchContainer}>
              <input
                type="text"
                className={styles.searchInput}
                placeholder={searchPlaceholder || t("SEARCH_PLACEHOLDER")}
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                onClick={(e) => e.stopPropagation()}
                autoFocus
              />
            </div>
          )}
          {selectedValues.length > 0 && (
            <div className={styles.clearContainer}>
              <button
                type="button"
                className={styles.clearButton}
                onClick={(e) => {
                  e.stopPropagation();
                  handleClear();
                }}
              >
                {t("CLEAR")}
              </button>
            </div>
          )}
          <div className={styles.optionsList}>
            {filteredOptions.length !== 0 ? (
              filteredOptions.map((option) => (
                <label key={option.value} className={styles.label}>
                  <input
                    type="checkbox"
                    className={styles.checkbox}
                    checked={selectedValues.includes(Number(option.value))}
                    onChange={() => handleCheckboxChange(Number(option.value))}
                  />
                  {option.label}
                </label>
              ))
            ) : (
              <div className={styles.noResults}>{t("NO_RESULTS")}</div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default CheckboxDropdown;
